import { useCallback, useEffect, useRef, useState } from "react";
import { remainingSeconds } from "./session";

// The Lock-In state machine: setup -> active -> finished. The countdown is derived from an end timestamp
// and re-checked whenever the page becomes visible again, so sleeping phones and throttled tabs stay right.
export default function useLockInSession({ now = Date.now, tickMs = 250 } = {}) {
  const [phase, setPhase] = useState("setup");
  const [session, setSession] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const sessionRef = useRef(null);
  const timerRef = useRef(null);

  const tick = useCallback(() => {
    const s = sessionRef.current;
    if (!s) return;
    const r = remainingSeconds(s.endAt, now());
    setRemaining(r);
    if (r <= 0) {
      clearInterval(timerRef.current);
      timerRef.current = null;
      setPhase("finished");
    }
  }, [now]);

  const start = useCallback(
    ({ minutes, task = null, sound = "off" }) => {
      const s = { minutes, task, sound, endAt: now() + minutes * 60_000 };
      sessionRef.current = s;
      setSession(s);
      setRemaining(minutes * 60);
      setPhase("active");
      clearInterval(timerRef.current);
      timerRef.current = setInterval(tick, tickMs);
    },
    [now, tick, tickMs]
  );

  const stop = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = null;
    sessionRef.current = null;
    setSession(null);
    setPhase("setup");
  }, []);

  const again = useCallback(() => {
    const s = sessionRef.current || session;
    if (s) start({ minutes: s.minutes, task: s.task, sound: s.sound });
  }, [session, start]);

  useEffect(() => {
    const onWake = () => {
      if (typeof document === "undefined" || document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    window.addEventListener("pageshow", onWake);
    return () => {
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
      window.removeEventListener("pageshow", onWake);
    };
  }, [tick]);

  useEffect(() => () => clearInterval(timerRef.current), []);

  return { phase, session, remaining, start, stop, again };
}
